import React from 'react';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import Typography from '@mui/material/Typography';

import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import styles from './LeadSidebarToursFromOtherLeads.module.css';
import { colors } from 'src/theme/colors';
import { Flex } from 'src/components/flex';
import ButtonLink from 'src/components/button-link/ButtonLink';
import ExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import Tour from 'src/components/lead-sidebar/LeadSidebarTour';
import { useLeadsByUuids } from 'src/services/api/leads/use-lead';

dayjs.extend(dayOfYear);

type Props = {
    lead: ILead;
    showings: IShowing[],
}

export default function LeadSidebarShowingsFromOtherLeads({
    lead,
    showings,
}: Props) {
    const now = dayjs();
    const today = now.dayOfYear();
    const currentYear = now.year();

    const toursFromOtherLeadsForSameProperty = showings
        // for the same property as the current lead
        .filter((tour) => tour.property_id === lead.property_id)
        // not for the current lead
        .filter((tour) => tour.lead_uuid !== lead.uuid)
        // not in the past
        .filter((tour) => dayjs(tour.start_time).year() == currentYear)
        .filter((tour) => dayjs(tour.start_time).dayOfYear() >= today)
        // sort by start time, most recent first
        .sort((a, b) => a.start_time.localeCompare(b.start_time));

    const numTours = toursFromOtherLeadsForSameProperty.length;
    const visibleTours = toursFromOtherLeadsForSameProperty.slice(0, 5);
    const { leadsByUuid: otherLeadsByUuid, isLoading: isLoadingLeads } = useLeadsByUuids(
        visibleTours.map((tour) => tour.lead_uuid),
    );

    return (
        <ExpandableItem
            title="Other Tours for this Lead's Property"
            defaultExpanded
            className={styles.flushRight}
        >
            {numTours > 0 && isLoadingLeads ? (
                <Flex className={styles.flushBottom}>
                    <Typography variant="body2" color="grey">Loading tours…</Typography>
                </Flex>
            ) : numTours ? (
                visibleTours
                    .filter((tour) => otherLeadsByUuid[tour.lead_uuid])
                    .map((tour) => (
                        <Tour
                            key={tour.id}
                            lead={otherLeadsByUuid[tour.lead_uuid]}
                            showing={tour}
                            color={colors.purple[500]}
                        />
                    ))
            ): (
                <Flex className={styles.flushBottom}>
                    <Typography variant="body2" color="grey">No Tours from other leads with same property</Typography>
                </Flex>
            )}

            {numTours > 5 && (
                <Typography variant="body2" color="textSecondary">
                    and {numTours - 5} more
                </Typography>
            )}

            <Flex justifyContent="flex-end">
                <ButtonLink
                    variant="outlined"
                    color="primary"
                    href="/landlord/inner/tours"
                    size="small"
                >
                    Go to Tours
                </ButtonLink>
            </Flex>
        </ExpandableItem>
    );
}
